import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothWiseSurvey = () => {
    const [booths, setBooths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const { appId, language } = useAppId();
    const navigate = useNavigate();

    // Safely get booth_no from local storage and append comma
    let localBoothNos = localStorage.getItem('boothNo') || localStorage.getItem('saktiKendraBooths') || "";
    
    // Append comma at the end if not already present
    if (localBoothNos && !localBoothNos.endsWith(',')) {
        localBoothNos = localBoothNos + ',';
    }
    
    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Booth Wise Survey",
            loadingData: "Loading Survey Data...",
            searchPlaceholder: "Search by Booth Number...",
            boothNo: "Booth No. :",
            survey: "Survey :",
            voter: "Voter :",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            cantSay: "Can't Say",
            noResults: "No booths found matching",
            noData: "No survey data available."
        },
        hi: {
            title: "बूथ वार सर्वे",
            loadingData: "सर्वे डेटा लोड हो रहा है...",
            searchPlaceholder: "बूथ नंबर द्वारा खोजें...",
            boothNo: "बूथ नं. :",
            survey: "सर्वे :",
            voter: "मतदाता :",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            cantSay: "कह नहीं सकते",
            noResults: "इससे मेल खाने वाला कोई बूथ नहीं मिला",
            noData: "कोई सर्वे डेटा उपलब्ध नहीं है।"
        },
        gu: {
            title: "બૂથ વાઇઝ સર્વે",
            loadingData: "સર્વે ડેટા લોડ થઈ રહ્યો છે...",
            searchPlaceholder: "બૂથ નંબર દ્વારા શોધો...",
            boothNo: "બૂથ નં. :",
            survey: "સર્વે :",
            voter: "મતદાર :",
            positive: "હકારાત્મક",
            negative: "નકારાત્મક",
            doubtful: "શંકાસ્પદ",
            cantSay: "કહી શકાય નહીં",
            noResults: "કોઈ બૂથ મળ્યું નથી",
            noData: "કોઈ સર્વે ડેટા ઉપલબ્ધ નથી."
        }
    };

    const txt = t[lang] || t.en;

    useEffect(() => {
        if (appId) {
            fetchBoothSurveyData();
        } else {
            navigate("/");
        }
    }, [appId, navigate]);

    const fetchBoothSurveyData = async () => {
        setLoading(true);
        try {
            const payload = {
                app_id: String(appId),
                booth_no: String(localBoothNos)
            };

            const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_survey_dash_for_saktikendra_pramukh`, payload);

            if (response.data.success && response.data.data && response.data.data.result) {
                setBooths(response.data.data.result);
            } else {
                setBooths([]);
            }
        } catch (err) {
            console.error("API Error:", err);
            setBooths([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredBooths = booths.filter(booth => {
        if (!searchTerm) return true;
        return booth.booth_no?.toString().includes(searchTerm);
    });

    if (loading) {
        return (
            <div className="app-container p-4">
                <Header title={txt.title} />
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #f8fafc; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #1e3a8a; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                `}</style>
            </div>
        );
    }

    return (
        <div className="app-container p-4">
            <Header title={txt.title} />

            {/* Professional Search Bar */}
            <div className="filter-container mt-4 mb-4">
                <div className="search-wrapper">
                    <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={txt.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Premium Booth Grid Matching the Provided Image Layout */}
            <div className="booth-grid">
                {filteredBooths.length > 0 ? (
                    filteredBooths.map((booth, index) => (
                        <div 
                            key={index} 
                            className="survey-card"
                            onClick={() => navigate('/dis_booth_wise_survey_voter', { state: { booth_no: booth.booth_no } })}
                        >
                            
                            {/* Top Header Row */}
                            <div className="card-header-row">
                                <h3 className="card-title">
                                    {txt.boothNo} {booth.booth_no}
                                </h3>
                                <h3 className="card-title">
                                    {txt.survey} {booth.total_survey || 0}
                                </h3>
                            </div>

                            {/* Sub Header Row */}
                            <div className="card-sub-row">
                                <span className="voter-text">{txt.voter} {booth.total_voter || 0}</span>
                            </div>

                            <div className="divider"></div>

                            {/* Bottom Stats Row */}
                            <div className="stats-box-grid">
                                <div className="stat-box box-positive">
                                    <span className="stat-label">{txt.positive}</span>
                                    <span className="stat-value val-positive">{booth.P || 0}</span>
                                </div>
                                <div className="stat-box box-negative">
                                    <span className="stat-label">{txt.negative}</span>
                                    <span className="stat-value val-negative">{booth.N || 0}</span>
                                </div>
                                <div className="stat-box box-doubtful">
                                    <span className="stat-label">{txt.doubtful}</span>
                                    <span className="stat-value val-doubtful">{booth.D || 0}</span>
                                </div>
                                <div className="stat-box box-cant-say">
                                    <span className="stat-label">{txt.cantSay}</span>
                                    <span className="stat-value val-cant-say">{booth.C || 0}</span>
                                </div>
                            </div>

                        </div>
                    ))
                ) : (
                    <div className="no-data-message">
                        <div className="no-data-icon">📝</div>
                        <div className="no-data-text">
                            {searchTerm ? `${txt.noResults} "${searchTerm}"` : txt.noData}
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- SEARCH BAR STYLES --- */
                .filter-container {
                    display: flex;
                    justify-content: center;
                    width: 100%;
                }

                .search-wrapper {
                    position: relative;
                    width: 100%;
                    max-width: 500px;
                }

                .search-icon {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    width: 20px;
                    height: 20px;
                }

                .search-input {
                    width: 100%;
                    padding: 14px 44px 14px 48px;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 15px;
                    color: #1e293b;
                    background-color: #ffffff;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                }

                .search-input:focus {
                    border-color: #1e3a8a;
                    box-shadow: 0 0 0 4px rgba(30, 58, 138, 0.1);
                }

                .clear-search-btn {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: #f1f5f9;
                    border: none;
                    color: #64748b;
                    padding: 6px;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }

                .clear-search-btn:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }

                /* --- BOOTH GRID & CARD STYLES EXACTLY MATCHING IMAGE --- */
                .booth-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
                    gap: 24px;
                }

                .survey-card {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 20px;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.02);
                    border: 1px solid #e2e8f0;
                    border-left: 6px solid #1e3a8a; /* Deep Blue Left Accent */
                    transition: all 0.3s ease;
                    cursor: pointer; /* Make it obviously clickable */
                }

                .survey-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
                }

                .card-header-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 4px;
                }

                .card-title {
                    font-size: 16px;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0;
                    letter-spacing: 0.2px;
                }

                .card-sub-row {
                    display: flex;
                    justify-content: flex-start;
                    margin-bottom: 12px;
                }

                .voter-text {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 500;
                }

                .divider {
                    width: 100%;
                    height: 1px;
                    background-color: #f1f5f9;
                    margin-bottom: 16px;
                }

                /* Four Stats Boxes */
                .stats-box-grid {
                    display: flex;
                    justify-content: space-between;
                    gap: 8px;
                    width: 100%;
                }

                .stat-box {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 12px 4px;
                    border-radius: 8px;
                    min-width: 0; /* allows shrinking on very small screens */
                }

                .stat-label {
                    font-size: 11px;
                    font-weight: 500;
                    color: #374151;
                    margin-bottom: 8px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                }

                .stat-value {
                    font-size: 16px;
                    font-weight: 800;
                }

                /* Colored Box Backgrounds & Text Colors */
                .box-positive { background-color: #f0fdf4; }
                .val-positive { color: #166534; }

                .box-negative { background-color: #fef2f2; }
                .val-negative { color: #991b1b; }

                .box-doubtful { background-color: #fffbeb; }
                .val-doubtful { color: #92400e; }

                .box-cant-say { background-color: #eff6ff; }
                .val-cant-say { color: #1e40af; }

                /* --- EMPTY STATE --- */
                .no-data-message {
                    grid-column: 1 / -1;
                    text-align: center;
                    padding: 60px 20px;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    border: 1px dashed #cbd5e1;
                }

                .no-data-icon {
                    font-size: 54px;
                    margin-bottom: 16px;
                }

                .no-data-text {
                    font-size: 16px;
                    font-weight: 600;
                    color: #64748b;
                }

                /* --- MOBILE RESPONSIVENESS --- */
                @media (max-width: 640px) {
                    .booth-grid {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }
                    
                    .stat-label {
                        font-size: 10px; /* slightly smaller on mobile to fit nicely */
                    }
                }
            `}</style>
        </div>
    );
};

export default BoothWiseSurvey;